import { MenuNode, TreeNode } from './types';

/** Synthetic container for menus whose parent code no longer exists. */
export const ORPHANS_CODE = '__ORPHANS__';

/**
 * Flat proc rows → nested tree, siblings ordered by MENU_SL then name.
 * Menus pointing at a deleted parent are collected under a synthetic
 * "orphans" folder at the bottom instead of polluting the root level —
 * dragging one onto a real menu re-parents (repairs) it.
 */
export const buildTree = (rows: MenuNode[]): TreeNode[] => {
  const byCode = new Map<string, TreeNode>();
  rows.forEach((row) =>
    byCode.set(row.MENU_CODE, { ...row, children: [], depth: 0 })
  );
  const roots: TreeNode[] = [];
  const orphans: TreeNode[] = [];
  byCode.forEach((node) => {
    if (!node.MENU_PARENT_CODE) {
      roots.push(node);
      return;
    }
    const parent = byCode.get(node.MENU_PARENT_CODE);
    if (parent && parent !== node) parent.children.push(node);
    else orphans.push(node);
  });
  if (orphans.length) {
    roots.push({
      MENU_CODE: ORPHANS_CODE,
      MENU_NAME: `Orphaned menus (${orphans.length}) — parent no longer exists`,
      MENU_PARENT_CODE: null,
      MENU_SL: 999999,
      MENU_TYPE: 'M',
      MENU_PROG_PATH: null,
      MENU_ICON_NAME: null,
      MENU_STATUS: 'O',
      MENU_MODE: null,
      MENU_REF: null,
      CHILD_COUNT: orphans.length,
      GROUP_COUNT: 0,
      children: orphans,
      depth: 0,
    });
  }
  const sortRec = (nodes: TreeNode[], depth: number) => {
    nodes.sort(
      (a, b) => a.MENU_SL - b.MENU_SL || a.MENU_NAME.localeCompare(b.MENU_NAME)
    );
    nodes.forEach((n) => {
      n.depth = depth;
      sortRec(n.children, depth + 1);
    });
  };
  sortRec(roots, 0);
  return roots;
};

/** Depth-first list of the nodes whose ancestors are all expanded. */
export const flattenVisible = (
  roots: TreeNode[],
  expanded: Set<string>
): TreeNode[] => {
  const out: TreeNode[] = [];
  const walk = (nodes: TreeNode[]) => {
    nodes.forEach((n) => {
      out.push(n);
      if (expanded.has(n.MENU_CODE)) walk(n.children);
    });
  };
  walk(roots);
  return out;
};

/** Does `candidate` sit inside `rootCode`'s subtree (incl. itself)? */
export const isInSubtree = (
  rows: MenuNode[],
  rootCode: string,
  candidate: string
): boolean => {
  if (rootCode === candidate) return true;
  const childrenOf = new Map<string, string[]>();
  rows.forEach((r) => {
    if (r.MENU_PARENT_CODE) {
      const list = childrenOf.get(r.MENU_PARENT_CODE) ?? [];
      list.push(r.MENU_CODE);
      childrenOf.set(r.MENU_PARENT_CODE, list);
    }
  });
  const stack = [...(childrenOf.get(rootCode) ?? [])];
  while (stack.length) {
    const code = stack.pop() as string;
    if (code === candidate) return true;
    stack.push(...(childrenOf.get(code) ?? []));
  }
  return false;
};

/** login statuses as the sign-in procedure treats them (Proc_App_Login) */
export const USER_STATUS: Record<string, { label: string; hint: string; color: 'success' | 'warning' | 'default' | 'error' | 'info'; canLogin: boolean }> = {
  O: { label: 'Active', hint: 'Can sign in', color: 'success', canLogin: true },
  F: { label: 'Must change password', hint: 'Can sign in; asked to choose a new password first', color: 'info', canLogin: true },
  T: { label: 'Temporary password', hint: 'Can sign in with a temporary password', color: 'info', canLogin: true },
  H: { label: 'Frozen', hint: 'Cannot sign in (on hold)', color: 'warning', canLogin: false },
  L: { label: 'Locked', hint: 'Cannot sign in (locked)', color: 'error', canLogin: false },
  C: { label: 'Closed', hint: 'Cannot sign in (closed, e.g. a leaver)', color: 'default', canLogin: false },
};

export const userStatusLabel = (status: string): string => USER_STATUS[status]?.label ?? status;

export const userStatusColor = (status: string): 'success' | 'warning' | 'default' | 'error' | 'info' =>
  USER_STATUS[status]?.color ?? 'default';

/** statuses an administrator can choose in the user form */
export const USER_STATUS_OPTIONS = ['O', 'F', 'H', 'L', 'C'] as const;

/** a portal page has a route path ("/..."); desktop ERP programs have form names; sections have none */
export const isPortalPage = (n: { MENU_PROG_PATH: string | null }) => !!n.MENU_PROG_PATH && n.MENU_PROG_PATH.startsWith('/');
export const isDesktopProgram = (n: { MENU_PROG_PATH: string | null; MENU_TYPE?: string }) =>
  !!n.MENU_PROG_PATH && !n.MENU_PROG_PATH.startsWith('/');
export const isLocked = (n: { MENU_STATUS: string | null }) => (n.MENU_STATUS ?? 'O') === 'L';

/** "Web - Payslip" -> "Payslip" (the sidebar shows portal menus without the prefix) */
export const displayMenuName = (name: string) => name.replace(/^Web\s*-\s*/i, '').replace(/\.+$/, '');

/** ancestors' names, e.g. "Human Resources › Payroll" */
export const menuPath = (rows: { MENU_CODE: string; MENU_PARENT_CODE: string | null; MENU_NAME: string }[], code: string, maxDepth = 12) => {
  const byCode = new Map(rows.map((r) => [r.MENU_CODE, r]));
  const names: string[] = [];
  let cur = byCode.get(code)?.MENU_PARENT_CODE ?? null;
  for (let i = 0; cur && i < maxDepth; i++) {
    const r = byCode.get(cur);
    if (!r) break;
    names.unshift(displayMenuName(r.MENU_NAME));
    cur = r.MENU_PARENT_CODE;
  }
  return names.join(' › ');
};

/** a plain sentence for a failed call: the server's own message, else what went wrong */
export const apiErrorText = (error: any): string => {
  const msg = error?.response?.data?.message;
  if (msg) return msg;
  const status = error?.response?.status;
  if (!error?.response) return 'The server could not be reached, so nothing was changed. Check the connection and try again.';
  if (status === 401) return 'Your session has ended. Sign in again; nothing was changed.';
  if (status === 403) return 'You do not have permission for this.';
  if (status === 404) return 'This function is not available on the server yet (it may need the latest update).';
  if (status >= 500) return `The server had a problem (error ${status}), so nothing was changed. Try again; if it keeps happening, tell IT.`;
  return error?.message || 'Something went wrong. Nothing was changed.';
};
